package com.mencanhealth.admin.ui

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.attributes.*
import com.mencanhealth.admin.model.*

@Composable
fun QuestionnaireStep(
    appointmentType: AppointmentType,
    answers: MutableMap<String, String>,
    onAnswerChange: (String, String) -> Unit
) {
    val questions = questionnaireForType(appointmentType)
    val sections = questions.map { it.section }.distinct()
    val answeredCount = questions.count { answers[it.id]?.isNotBlank() == true }
    val totalCount = questions.size
    val progressPercent = if (totalCount > 0) (answeredCount * 100) / totalCount else 0

    H3 { Text("Cuestionario previo a tu cita") }
    P({ style { color(Color("#666")); property("margin-bottom", "24px"); property("font-size", "14px") } }) {
        Text("Completa este cuestionario para que tu consulta sea más productiva.")
    }

    // Progress bar
    Div({ classes("questionnaire-progress") }) {
        Div({
            style {
                property("width", "${progressPercent}%")
                property("height", "100%")
                property("background", "linear-gradient(135deg, #00e676, #00c853)")
                property("border-radius", "8px")
                property("transition", "width 0.3s")
            }
        }) {}
    }
    P({ style { property("font-size", "13px"); color(Color("#999")); property("margin-bottom", "32px"); textAlign("center") } }) {
        Text("$answeredCount de $totalCount preguntas contestadas ($progressPercent%)")
    }

    // Sections
    sections.forEachIndexed { sectionIdx, sectionName ->
        val sectionQuestions = questions.filter { it.section == sectionName }
        val sectionIcon = sectionQuestions.first().sectionIcon

        Div({ classes("questionnaire-section") }) {
            Div({ classes("section-indicator") }) {
                Img(src = sectionIcon, alt = "") { style { property("width", "40px"); property("height", "40px"); property("object-fit", "contain") } }
                Div {
                    H4({ style { property("margin", "0"); property("font-size", "16px"); color(Color("#1a1a2e")) } }) {
                        Text(sectionName)
                    }
                    Span({ style { property("font-size", "12px"); color(Color("#999")) } }) {
                        Text("Sección ${sectionIdx + 1} de ${sections.size}")
                    }
                }
            }

            sectionQuestions.forEach { q ->
                Div({ style { property("margin-bottom", "20px") } }) {
                    Span({ classes("form-label") }) {
                        Text(q.question)
                        if (q.required) {
                            Span({ style { color(Color("#c62828")) } }) { Text(" *") }
                        }
                    }

                    when (q.type) {
                        QuestionType.TEXT -> {
                            if (q.placeholder.length > 50) {
                                TextArea(attrs = {
                                    classes("form-input")
                                    attr("placeholder", q.placeholder)
                                    attr("rows", "3")
                                    onInput { onAnswerChange(q.id, it.value) }
                                }, value = answers[q.id] ?: "")
                            } else {
                                Input(InputType.Text) {
                                    classes("form-input")
                                    value(answers[q.id] ?: "")
                                    attr("placeholder", q.placeholder)
                                    onInput { onAnswerChange(q.id, it.value) }
                                }
                            }
                        }

                        QuestionType.NUMBER -> {
                            Input(InputType.Number) {
                                classes("form-input")
                                value(answers[q.id] ?: "")
                                attr("placeholder", q.placeholder)
                                attr("min", q.min.toString())
                                attr("max", q.max.toString())
                                onInput { onAnswerChange(q.id, it.value?.toString() ?: "") }
                            }
                        }

                        QuestionType.SELECT -> {
                            Div({ classes("type-selector") }) {
                                q.options.forEach { option ->
                                    val isSelected = answers[q.id] == option
                                    Div({
                                        classes("type-card")
                                        if (isSelected) classes("type-card-selected")
                                        onClick { onAnswerChange(q.id, option) }
                                        style { property("padding", "12px 16px") }
                                    }) {
                                        Span({ style { property("font-size", "14px") } }) { Text(option) }
                                    }
                                }
                            }
                        }

                        QuestionType.MULTI_SELECT -> {
                            val currentSelections = (answers[q.id] ?: "").split("|||").filter { it.isNotBlank() }
                            Div({ style { display(DisplayStyle.Flex); property("gap", "8px"); flexWrap(FlexWrap.Wrap) } }) {
                                q.options.forEach { option ->
                                    val isSelected = option in currentSelections
                                    Button({
                                        classes("chip")
                                        if (isSelected) classes("chip-active")
                                        onClick {
                                            val updated = if (isSelected) {
                                                currentSelections.filter { it != option }
                                            } else {
                                                currentSelections + option
                                            }
                                            onAnswerChange(q.id, updated.joinToString("|||"))
                                        }
                                    }) { Text(option) }
                                }
                            }
                        }

                        QuestionType.SLIDER -> {
                            val currentVal = answers[q.id] ?: ((q.min + q.max) / 2).toString()
                            Div({ classes("slider-container") }) {
                                Div({ style { display(DisplayStyle.Flex); property("justify-content", "space-between"); property("margin-bottom", "4px") } }) {
                                    Span({ style { property("font-size", "12px"); color(Color("#999")) } }) { Text(q.min.toString()) }
                                    Span({ style { property("font-size", "18px"); property("font-weight", "700"); color(Color("#283593")) } }) { Text(currentVal) }
                                    Span({ style { property("font-size", "12px"); color(Color("#999")) } }) { Text(q.max.toString()) }
                                }
                                Input(InputType.Range) {
                                    classes("form-input")
                                    attr("min", q.min.toString())
                                    attr("max", q.max.toString())
                                    value(currentVal)
                                    onInput { onAnswerChange(q.id, it.value?.toString() ?: "") }
                                    style { property("width", "100%") }
                                }
                            }
                        }

                        QuestionType.EMOJI_SELECT -> {
                            Div({ style { display(DisplayStyle.Flex); property("gap", "8px"); flexWrap(FlexWrap.Wrap) } }) {
                                q.options.forEach { option ->
                                    val isSelected = answers[q.id] == option
                                    val emoji = option.substringBefore(" ")
                                    val label = option.substringAfter(" ")
                                    Button({
                                        classes("emoji-option")
                                        if (isSelected) classes("emoji-option-selected")
                                        onClick { onAnswerChange(q.id, option) }
                                    }) {
                                        Span({ style { property("font-size", "24px"); display(DisplayStyle.Block) } }) { Text(emoji) }
                                        Span({ style { property("font-size", "11px") } }) { Text(label) }
                                    }
                                }
                            }
                        }
                    }

                    if (q.helpText.isNotBlank()) {
                        P({ style { property("font-size", "12px"); color(Color("#999")); property("margin-top", "4px"); property("font-style", "italic") } }) {
                            Text(q.helpText)
                        }
                    }
                }
            }
        }
    }
}
